import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

export type PopoverProps = {
  /** The visible text of the button that opens the panel. */
  label: ReactNode
  /** The panel heading, which also names it. */
  title: string
  /** The panel content: text, a link, a short list. */
  children: ReactNode
  /** The heading level for the title, to fit the page outline. */
  headingLevel?: 2 | 3 | 4
  /** Which edge of the button the panel lines up with. */
  align?: 'start' | 'end'
  /** Classes for the button. */
  buttonClassName?: string
}

/**
 * A non-modal panel that a button shows and hides, for richer content than a
 * tooltip. It follows the disclosure pattern: the button carries
 * aria-expanded and aria-controls, focus stays on the button when it opens,
 * and the panel follows it in the tab order. Escape closes it and returns
 * focus to the button; a click outside closes it.
 */
export default function Popover({
  label,
  title,
  children,
  headingLevel = 3,
  align = 'start',
  buttonClassName = 'mds-button mds-button--secondary',
}: PopoverProps) {
  const panelId = useId()
  const titleId = useId()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)
  const Heading = `h${headingLevel}` as const

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      button.current?.focus()
    }
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [open])

  return (
    <div ref={root} className={`mds-popover mds-popover--${align}`}>
      <button
        ref={button}
        type="button"
        className={buttonClassName}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
      </button>
      <div id={panelId} className="mds-popover__panel" role="group" aria-labelledby={titleId} hidden={!open}>
        <Heading id={titleId} className="mds-popover__title">
          {title}
        </Heading>
        <div className="mds-popover__body">{children}</div>
      </div>
    </div>
  )
}
